import { logDebug, logError } from '../../shared/utils/logger';
import { BASE_URL, fetchWithAuth } from './client';

/**
 * P&L items for a case
 * RQ: GET /api/cases/{caseId}/pnl/items
 * RS: { status: 200, data: { items: Array<{ id, description, amount, type, createdAt, updatedAt }> } }
 */
export async function fetchPnlItems(caseId) {
    if (!caseId) {
        throw new Error("Unable to load P&L items. Please try again.");
    }
    const url = `${BASE_URL}/api/cases/${encodeURIComponent(caseId)}/pnl/items`;
    if (process.env.NODE_ENV !== "production") {
        logDebug("P&L items request:", { url, caseId });
    }
    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });
    if (process.env.NODE_ENV !== "production") {
        const responseClone = response.clone();
        let debugBody = null;
        try {
            debugBody = await responseClone.json();
        } catch (error) {
            try {
                debugBody = await responseClone.text();
            } catch (textError) {
                debugBody = null;
            }
        }
        logDebug("P&L items response:", { status: response.status, body: debugBody });
    }
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch P&L items failed:", response.status, errorText);
        throw new Error("Unable to load P&L items. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Create P&L item for a case
 * RQ: { caseId: string, description: string, amount: number, type: "revenue" | "expense" }
 * RS: { status: 201, data: { id, description, amount, type, createdAt, updatedAt } }
 */
export async function createPnlItem(payload) {
    const url = `${BASE_URL}/api/cases/${encodeURIComponent(payload.caseId)}/pnl/items`;
    if (process.env.NODE_ENV !== "production") {
        logDebug("Create P&L item request:", { url, body: payload });
    }
    const response = await fetchWithAuth(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            description: payload.description,
            amount: payload.amount,
            type: payload.type
        })
    });
    if (process.env.NODE_ENV !== "production") {
        const responseClone = response.clone();
        let debugBody = null;
        try {
            debugBody = await responseClone.json();
        } catch (error) {
            try {
                debugBody = await responseClone.text();
            } catch (textError) {
                debugBody = null;
            }
        }
        logDebug("Create P&L item response:", { status: response.status, body: debugBody });
    }
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Create P&L item failed:", response.status, errorText);
        throw new Error("Unable to create P&L item. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Update P&L item
 * RQ: { itemId: string, description?: string, amount?: number, type?: "revenue" | "expense" }
 * RS: { status: 200, data: { id, description, amount, type, updatedAt } }
 */
export async function updatePnlItem(payload) {
    const url = `${BASE_URL}/api/pnl/items/${encodeURIComponent(payload.itemId)}`;
    if (process.env.NODE_ENV !== "production") {
        logDebug("Update P&L item request:", { url, body: payload });
    }
    const response = await fetchWithAuth(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            description: payload.description,
            amount: payload.amount,
            type: payload.type
        })
    });
    if (process.env.NODE_ENV !== "production") {
        const responseClone = response.clone();
        let debugBody = null;
        try {
            debugBody = await responseClone.json();
        } catch (error) {
            try {
                debugBody = await responseClone.text();
            } catch (textError) {
                debugBody = null;
            }
        }
        logDebug("Update P&L item response:", { status: response.status, body: debugBody });
    }
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Update P&L item failed:", response.status, errorText);
        throw new Error("Unable to update P&L item. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Delete P&L item
 * RQ: { itemId: string }
 * RS: { status: 200, data: { deleted: true } }
 */
export async function deletePnlItem(payload) {
    const url = `${BASE_URL}/api/pnl/items/${encodeURIComponent(payload.itemId)}`;
    if (process.env.NODE_ENV !== "production") {
        logDebug("Delete P&L item request:", { url, body: payload });
    }
    const response = await fetchWithAuth(url, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" }
    });
    if (process.env.NODE_ENV !== "production") {
        const responseClone = response.clone();
        let debugBody = null;
        try {
            debugBody = await responseClone.json();
        } catch (error) {
            try {
                debugBody = await responseClone.text();
            } catch (textError) {
                debugBody = null;
            }
        }
        logDebug("Delete P&L item response:", { status: response.status, body: debugBody });
    }
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Delete P&L item failed:", response.status, errorText);
        throw new Error("Unable to delete P&L item. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * P&L summary totals for a case
 * RQ: GET /api/cases/{caseId}/pnl/summary
 * RS: { status: 200, data: { totalRevenue: number, totalExpenses: number, net: number } }
 */
export async function fetchPnlSummary(caseId) {
    if (!caseId) {
        throw new Error("Unable to load P&L summary. Please try again.");
    }
    const response = await fetchWithAuth(`${BASE_URL}/api/cases/${encodeURIComponent(caseId)}/pnl/summary`, {
        headers: { accept: "application/json" }
    });
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch P&L summary failed:", response.status, errorText);
        throw new Error("Unable to load P&L summary. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}

/**
 * Portfolio P&L summary for a lender
 * RQ: GET /api/pnl/summary?userId={email}&periodStart?&periodEnd?
 * RS: { status: 200, data: { totalRevenue, totalExpenses, net, caseCount, avgRevenuePerCase, avgNetPerCase, netMargin } }
 */
export async function fetchPortfolioPnlSummary({ userId, periodStart, periodEnd }) {
    if (!userId) {
        throw new Error("Unable to load portfolio P&L summary. Please try again.");
    }
    const params = new URLSearchParams({ userId });
    if (periodStart) params.set("periodStart", periodStart);
    if (periodEnd) params.set("periodEnd", periodEnd);
    const url = `${BASE_URL}/api/pnl/summary?${params.toString()}`;
    const response = await fetchWithAuth(url, {
        headers: { accept: "application/json" }
    });
    if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        logError("Fetch portfolio P&L summary failed:", response.status, errorText);
        throw new Error("Unable to load portfolio P&L summary. Please try again.");
    }
    const data = await response.json();
    return { status: response.status, data };
}
